import {Injectable} from '@angular/core';
import * as Moment from "moment";
import {unitOfTime} from "moment";
import * as mTZ from "moment-timezone";
import * as moment from "moment-timezone";
import * as Highcharts from "highcharts";
import {Period} from "../components/machine-details/components/machine-details-data/machine-details-data.component";

window.moment = Moment;
mTZ()

@Injectable({
  providedIn: 'root'
})
export class TimezoneService {

  public currentTimezone: string;

  private readonly DATE_FORMAT: string = 'DD/MM/YYYY';
  private readonly LANGUAGE: string = 'es-ES';
  private readonly DAY: unitOfTime.StartOf = 'day';
  private readonly NUMERIC = 'numeric';

  private offset: string;


  constructor() {
    this.currentTimezone = moment.tz.guess();
    Highcharts.dateFormats['Z'] = (time: number): string => this.offset;
    this.timezoneChanged();
  }

  public timestampRangeFromCalendarData(rangeDates: Date[]): { from: number, until: number } {
    let from: number = moment.tz(rangeDates[0].toLocaleDateString(this.LANGUAGE), this.DATE_FORMAT, this.currentTimezone).startOf(this.DAY).valueOf();
    let until: number = moment.tz(rangeDates[1].toLocaleDateString(this.LANGUAGE), this.DATE_FORMAT, this.currentTimezone).endOf(this.DAY).valueOf();
    return {from: from, until: until};
  }

  public timestampFromCalendarDataWithTime(date: Date): number {
    let dateString = date.toLocaleDateString(this.LANGUAGE, {
      hour: this.NUMERIC,
      minute: this.NUMERIC,
      second: this.NUMERIC
    });
    return moment.tz(dateString, 'DD/MM/YYYY, HH:mm:ss', this.currentTimezone).valueOf();
  }

  public getAllTimezones(): { name: string }[] {
    return moment.tz.names().map(zoneName => ({name: zoneName}));
  }

  public guessTimezone(): void {
    this.currentTimezone = moment.tz.guess();
    this.timezoneChanged();
  }

  getCurrentTimezone(): string {
    return this.currentTimezone;
  }

  setTimezone(newTimezone: string) {
    this.currentTimezone = newTimezone;
    this.timezoneChanged();
  }

  timezoneChanged(): void {
    this.offset = moment.tz(moment.utc(), this.currentTimezone).format('Z');
    Highcharts.setOptions(
      {
        time: {
          timezone: this.currentTimezone
        }
      }
    );
  }

  public getHoursOfDay(): string[] {
    return ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19',
      '20', '21', '22', '23'];
  }

  public getPeriodRange(period: Period, periodRangeValid: boolean, periodRange?: [any, any]): [number, number] {
    let periodRangeInTimezone: { from: number, until: number };
    if (period === 'lastDay') {
      let startOfDay = new Date();
      startOfDay.setHours(0, 0, 0, 0);
      periodRangeInTimezone = this.timestampRangeFromCalendarData([this.subtractDays(1, startOfDay), startOfDay]);
    } else if (period === 'lastWeek') {
      let startOfDay = new Date();
      let startOfWeek = this.subtractDays(startOfDay.getDay() - 1, startOfDay);
      startOfWeek.setHours(0, 0, 0, 0);
      periodRangeInTimezone = this.timestampRangeFromCalendarData([startOfWeek, new Date()]);
    } else if (period === 'lastMonth') {
      let startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);
      periodRangeInTimezone = this.timestampRangeFromCalendarData([startOfMonth, new Date()]);
    } else if (period === 'specificPeriod' && periodRangeValid) {
      periodRangeInTimezone = this.timestampRangeFromCalendarData([periodRange[0], periodRange[1]]);
    } else {
      periodRangeInTimezone = {from: null, until: null};
    }
    return [periodRangeInTimezone.from, periodRangeInTimezone.until];
  }

  private subtractDays(numOfDays, date: Date): Date {
    date.setDate(date.getDate() - numOfDays);
    return date;
  }

  public calculateDurationExcel(activationDate: string, deactivationDate: string, daysTranslation: string, hoursTranslation: string,
                                minutesTranslation: string, secondsTranslation: string): string {
    if (deactivationDate != null) {
      const timeInMilliSeconds: number = (new Date(deactivationDate).getTime() - new Date(activationDate).getTime());
      return this.milliSecondsToTime(timeInMilliSeconds, daysTranslation, hoursTranslation, minutesTranslation, secondsTranslation);
    } else return '';
  }

  public milliSecondsToTime(milliSeconds: number, daysTranslation: string, hoursTranslation: string,
                            minutesTranslation: string, secondsTranslation: string): string {
    if (milliSeconds != 0) {
      let seconds: number = milliSeconds / 1000;
      let minutes: number = seconds / 60;
      let hours: number = minutes / 60;
      let days: number = hours / 24;

      let duration: string = this.timeFormatterDaysHoursAndMinutes(Math.floor(days), Math.floor(hours) % 24,
        Math.floor(minutes) % 60, daysTranslation, hoursTranslation, minutesTranslation);
      return this.timeFormatter(Math.floor(days), Math.floor(hours) % 24, Math.floor(minutes) % 60,
        Math.floor(seconds) % 60, duration, secondsTranslation);
    }
    return '0 ' + secondsTranslation;
  }

  private timeFormatter(days: number, hours: number, minutes: number, seconds: number, duration: string,
                        secondsTranslation: string): string {
    if (seconds > 0) {
      if (days > 0 || hours > 0 || minutes > 0) duration = duration + ' ';
      duration = duration + seconds + ' ' + secondsTranslation;
    }
    if (duration === '') {
      duration = seconds + ' ' + secondsTranslation;
    }
    return duration;
  }

  public timeFormatterDaysHoursAndMinutes(days: number, hours: number, minutes: number, daysTranslation: string,
                                          hoursTranslation: string, minutesTranslation: string): string {
    let duration: string = '';
    if (days > 0) duration = days + ' ' + daysTranslation;
    if (hours > 0) {
      if (days > 0) duration = duration + ' ';
      duration = duration + hours + ' ' + hoursTranslation;
    }
    if (minutes > 0) {
      if (days > 0 || hours > 0) duration = duration + ' ';
      duration = duration + minutes + ' ' + minutesTranslation;
    }
    return duration;
  }

  public minutesToTime(minutes: number, hoursTranslation: string, minutesTranslation: string): string {
    if (minutes != 0) {
      let hours = minutes / 60;

      return this.timeFormatterHoursAndMinutes(Math.floor(hours) % 24, Math.floor(minutes) % 60,
        hoursTranslation, minutesTranslation);
    }
    return '0 ' + minutesTranslation;
  }

  private timeFormatterHoursAndMinutes(hours: number, minutes: number, hoursTranslation: string,
                                       minutesTranslation: string): string {
    let duration: string = '';
    if (hours > 0) duration = hours + ' ' + hoursTranslation;
    if (minutes > 0) {
      if (hours > 0) duration = duration + ' ';
      duration = duration + minutes + ' ' + minutesTranslation;
    }
    if (duration === '') {
      duration = minutes + ' ' + minutesTranslation;
    }
    return duration;
  }

  public calcYearRange(): string {
    let current: number = new Date().getFullYear();
    return `${current - 10}:${current}`;
  }

}
